import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Clock, Package, Truck, MapPin, ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import { checkoutClient } from '@happyspace/sdk/restaurant';

const statusSteps = [
  { key: 'confirmed', label: 'Order Confirmed', icon: CheckCircle2 },
  { key: 'preparing', label: 'Preparing', icon: Package },
  { key: 'ready', label: 'Ready', icon: Clock },
  { key: 'shipped', label: 'Out for Delivery', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: MapPin },
];

export default function OrderStatusPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const fetchOrder = async () => {
    if (!orderId) return;
    try {
      setLoading(true);
      const result = await checkoutClient.getOrder(orderId);
      setOrder(result);
      setLastUpdated(new Date());
      setError(null);
    } catch (err: any) {
      setError('Unable to load order details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
    const interval = setInterval(fetchOrder, 30000);
    return () => clearInterval(interval);
  }, [orderId]);

  const currentStepIndex = order
    ? statusSteps.findIndex(s => s.key === (order.status || 'confirmed'))
    : 0;
  const activeIndex = currentStepIndex === -1 ? 0 : currentStepIndex;

  return (
    <div className="pt-20 min-h-screen bg-[#F9F6F0]">
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-8 font-semibold group">
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" /> Back to Home
        </Link>

        {loading && !order ? (
          <div className="text-center py-24">
            <Loader2 className="w-10 h-10 text-[#0054e9] animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Loading your order...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-3xl p-10 text-center">
            <p className="text-red-600 font-semibold mb-4">{error}</p>
            <button onClick={fetchOrder} className="bg-[#0054e9] text-white px-6 py-3 rounded-full font-bold">
              Try Again
            </button>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {/* Header card */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 mb-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Order Status</h1>
                  <p className="text-gray-500 text-sm mt-1">Order #{orderId?.slice(-8).toUpperCase()}</p>
                </div>
                <button
                  onClick={fetchOrder}
                  className="flex items-center gap-2 text-sm text-[#0054e9] font-semibold hover:opacity-70"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              {/* Status steps */}
              <div className="relative">
                <div className="absolute top-5 left-5 right-5 h-0.5 bg-gray-200 z-0" />
                <div
                  className="absolute top-5 left-5 h-0.5 bg-[#0054e9] z-0 transition-all duration-700"
                  style={{ width: `${(activeIndex / (statusSteps.length - 1)) * 100}%` }}
                />
                <div className="relative flex justify-between">
                  {statusSteps.map((step, i) => {
                    const isComplete = i <= activeIndex;
                    const isCurrent = i === activeIndex;
                    return (
                      <div key={step.key} className="flex flex-col items-center gap-2">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all ${
                          isComplete ? 'bg-[#0054e9] text-white' : 'bg-white border-2 border-gray-200 text-gray-300'
                        } ${isCurrent ? 'ring-4 ring-[#0054e9]/20' : ''}`}>
                          <step.icon className="w-5 h-5" />
                        </div>
                        <span className={`text-xs font-semibold text-center leading-tight ${
                          isComplete ? 'text-[#0054e9]' : 'text-gray-400'
                        }`}>{step.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {order?.estimatedDelivery && (
                <div className="mt-6 p-4 bg-blue-50 rounded-2xl text-center">
                  <p className="text-sm text-[#0054e9] font-semibold">
                    Estimated Delivery: {new Date(order.estimatedDelivery).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              )}
            </div>

            {/* Order items */}
            {order?.items && order.items.length > 0 && (
              <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 mb-6">
                <h2 className="font-bold text-gray-900 mb-4">Order Summary</h2>
                <div className="space-y-3">
                  {order.items.map((item: any, i: number) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-gray-700">{item.quantity}x {item.name}</span>
                      <span className="font-semibold">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  {order.total && (
                    <div className="border-t border-gray-100 pt-3 flex justify-between font-bold">
                      <span>Total</span>
                      <span>${order.total.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tracking info */}
            {order?.trackingNumber && (
              <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
                <h3 className="font-bold text-gray-900 mb-2">Tracking</h3>
                <p className="text-sm text-gray-500">Tracking #: <span className="font-mono font-semibold text-gray-900">{order.trackingNumber}</span></p>
              </div>
            )}

            <p className="text-center text-xs text-gray-400 mt-6">Last updated: {lastUpdated.toLocaleTimeString()}</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
